import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getUniversities, getStudents } from '../services/apiService';
import { 
  Search, 
  MapPin, 
  Globe, 
  Award,
  ArrowRight,
  Loader2,
  X,
  User,
  GraduationCap,
  FileText
} from 'lucide-react';
const PartneredUniversities = () => {

  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [universityStudents, setUniversityStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Fetch universities from database
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoading(true);
        const data = await getUniversities();
        // Filter out universities with blob URLs (but keep them, just don't display invalid logos)
        setUniversities(data);
      } catch (error) {
        console.error('Error fetching universities:', error);
        // Fallback to empty array if fetch fails
        setUniversities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUniversities();
  }, []);

  // Filter Logic
  const filteredUnis = universities.filter(uni => {
    const matchesCountry = filter === 'All' || uni.country === filter;
    const matchesSearch = uni.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCountry && matchesSearch;
  });

  // Extract unique countries from universities
  const uniqueCountries = ['All', ...new Set(universities.map(u => u.country).filter(Boolean))];
  const countries = uniqueCountries.length > 1 ? uniqueCountries : ['All', 'USA', 'UK', 'Canada', 'Australia', 'Germany', 'New Zealand', 'Ireland'];

  // Handle university click
  const handleUniversityClick = async (university) => {
    setSelectedUniversity(university);
    setLoadingStudents(true);
    try {
      const students = await getStudents(university.id);
      setUniversityStudents(students);
    } catch (error) {
      console.error('Error loading students:', error);
      setUniversityStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      
      
      {/* =========================================
          HERO: SEARCH FOCUSED
      ========================================= */}
      <section className="relative pt-32 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-6">
              World Class Partners.
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              We have direct tie-ups with 500+ universities. <br/>
              Your application gets priority status through our network.
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative mb-12">
              <input 
                type="text" 
                placeholder="Search by university name..." 
                className="w-full pl-12 pr-4 py-4 rounded-full border border-slate-300 focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none shadow-lg shadow-slate-200/50 text-lg transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-2">
              {countries.map(country => (
                <button
                  key={country}
                  onClick={() => setFilter(country)}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                    filter === country 
                      ? 'bg-slate-900 text-white shadow-lg' 
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600'
                  }`}
                >
                  {country}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          UNIVERSITY GRID
      ========================================= */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-red-600" size={48} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <AnimatePresence>
                {filteredUnis.map((uni, i) => {
                  // Determine logo source: uploaded logo > Clearbit API > fallback
                  const logoUrl = uni.logo_url || uni.logo;
                  const logoSource = logoUrl && !logoUrl.startsWith('blob:') 
                    ? logoUrl 
                    : (uni.domain ? `https://logo.clearbit.com/${uni.domain}` : null);
                  
                  return (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                      key={uni.id || i}
                      onClick={() => handleUniversityClick(uni)}
                      className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-red-200 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col items-center text-center cursor-pointer"
                    >
                      {/* Rank Badge */}
                      {uni.rank && (
                        <div className="self-end mb-4">
                          <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-100 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            <Award size={10} className="text-red-500" /> {uni.rank}
                          </span>
                        </div>
                      )}

                      {/* Logo: Uploaded logo, Clearbit API, or fallback */}
                      <div className="w-20 h-20 mb-6 p-2 bg-white rounded-full border border-slate-100 shadow-inner flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                        {logoSource ? (
                          <img 
                            src={logoSource}
                            alt={uni.name}
                            className="w-full h-full object-contain filter grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 transition-all duration-500"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextElementSibling) {
                                e.target.nextElementSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        {/* Fallback Icon */}
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200 rounded-full" style={{ display: logoSource ? 'none' : 'flex' }}>
                          <Globe size={32} className="text-red-600" />
                        </div>
                      </div>

                      <h3 className="text-lg font-serif font-bold text-slate-900 mb-2 leading-tight group-hover:text-red-700 transition-colors">
                        {uni.name}
                      </h3>
                      
                      {uni.country && (
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mt-auto">
                          <MapPin size={12} /> {uni.country}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* Empty State */}
          {filteredUnis.length === 0 && (
            <div className="text-center py-20 opacity-50">
              <Globe size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-xl font-serif text-slate-500">No universities found matching criteria.</p>
            </div>
          )}

        </div>
      </section>

      {/* =========================================
          CTA: "WE APPLY EVERYWHERE"
      ========================================= */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-bold text-slate-900 mb-4">
            Don't see your dream university?
          </h2>
          <p className="text-slate-600 mb-8 leading-relaxed">
            This list is just a glimpse. We assist with applications to 
            <span className="font-bold text-slate-900"> any accredited university </span> 
            in the UK, USA, Canada, Australia, and Europe.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border-b-2 border-red-600 text-slate-900 font-bold hover:text-red-600 transition-colors pb-1"
          >
            Request Specific University Info <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Students Modal */}
      {selectedUniversity && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedUniversity(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{selectedUniversity.name}</h2>
                <p className="text-sm text-slate-600 mt-1">{selectedUniversity.country}</p>
              </div>
              <button
                onClick={() => setSelectedUniversity(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto">
              {loadingStudents ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-red-600" size={32} />
                </div>
              ) : universityStudents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {universityStudents.map((student) => (
                    <div key={student.id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                          {student.photo_url && !student.photo_url.startsWith('blob:') ? (
                            <img src={student.photo_url} alt={student.name} className="w-full h-full object-cover" />
                          ) : (
                            <User size={20} className="text-slate-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">{student.name}</h3>
                          <p className="text-sm text-slate-600">{student.course}</p>
                        </div>
                      </div>
                      {student.visa_type && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <FileText size={14} />
                          <span>{student.visa_type}</span>
                        </div>
                      )}
                      {student.status && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          student.status === 'graduated' ? 'bg-green-100 text-green-700' :
                          student.status === 'enrolled' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {student.status}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <GraduationCap size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No students placed at this university yet.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
};

export default PartneredUniversities;